import React from 'react';
import { 
  getQuizScoreBgColorClass, 
  getQuizScoreTextColorClass,
  getCheatingProbabilityBgColorClass,
  getCheatingProbabilityTextColorClass,
  getCheatingProbabilityLabel
} from '../../Utils/ColorUtils';

/**
 * EvaluationsOverview Component
 * Displays an overview of all evaluations with scores and metrics
 * Simplified version showing only the overview section
 */
const EvaluationsOverview = ({ evals }) => {
  // Extract all challenges from evals
  const extractChallenges = () => {
    if (!evals || evals.length === 0) return [];
    
    const challenges = [];
    
    evals.forEach(evalItem => {
      if (evalItem.challenge_instance && Array.isArray(evalItem.challenge_instance)) {
        challenges.push(...evalItem.challenge_instance);
      }
    });
    
    console.log("evals", evals);

    return challenges;
  };
  
  // Extract individual scores from all challenges
  const extractIndividualScores = () => {
    const challenges = extractChallenges();
    const scores = [];
    
    challenges.forEach(challenge => {
      if (challenge.challenge_final_json && challenge.challenge_final_json.totalScore) {
        // Handle both string and number types for totalScore
        const score = parseFloat(challenge.challenge_final_json.totalScore);
        if (!isNaN(score)) {
          scores.push(score);
        }
      }
    });
    
    return scores;
  };
  
  // Extract individual cheating probabilities from all challenges
  const extractIndividualCheatingProbabilities = () => {
    const challenges = extractChallenges();
    const probabilities = [];
    
    challenges.forEach(challenge => {
      if (challenge.challenge_cheating_assessment_json && challenge.challenge_cheating_assessment_json.cheatingProbability) {
        // Extract numeric value from string (e.g., "70%" -> 70)
        const probabilityStr = challenge.challenge_cheating_assessment_json.cheatingProbability;
        const probabilityNum = parseInt(probabilityStr.replace('%', ''));
        
        if (!isNaN(probabilityNum)) {
          probabilities.push(probabilityNum);
        }
      }
    });
    
    return probabilities;
  };
  
  // Calculate average score from all challenges
  const calculateAverageScore = () => {
    const scores = extractIndividualScores();
    if (scores.length === 0) return 0;
    
    const totalScore = scores.reduce((sum, score) => sum + score, 0);
    return Math.round(totalScore / scores.length);
  };
  
  // Calculate average cheating probability from all challenges
  const calculateAverageCheatingProbability = () => {
    const probabilities = extractIndividualCheatingProbabilities();
    if (probabilities.length === 0) return 0;
    
    const totalProbability = probabilities.reduce((sum, prob) => sum + prob, 0);
    return Math.round(totalProbability / probabilities.length);
  };
  
  // Format individual scores as a string
  const formatIndividualScores = () => {
    const scores = extractIndividualScores();
    if (scores.length === 0) return "";
    
    const scoresStr = scores.map(score => `${score}%`).join(', ');
    return `${scores.length} challenge${scores.length !== 1 ? 's' : ''} (${scoresStr})`;
  };
  
  // Format individual cheating probabilities as a string
  const formatIndividualCheatingProbabilities = () => {
    const probabilities = extractIndividualCheatingProbabilities();
    if (probabilities.length === 0) return "";
    
    const labelsStr = probabilities.map(prob => getCheatingProbabilityLabel(prob)).join(', ');
    return `${probabilities.length} challenge${probabilities.length !== 1 ? 's' : ''} (${labelsStr})`;
  };
  
  const averageScore = calculateAverageScore();
  const averageCheatingProbability = calculateAverageCheatingProbability();

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-xl font-semibold text-gray-800">Evaluations Overview</h4>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Overall Score Column */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col md:flex-row">
            {/* Score Display - Left Side */}
            <div className="md:w-1/3 flex flex-col items-start mb-4 md:mb-0">
              <div className="w-full max-w-[200px]">
                <div className={`text-center p-6 rounded-lg ${getQuizScoreBgColorClass(averageScore)}`}>
                  <p className={`text-5xl font-bold ${getQuizScoreTextColorClass(averageScore)}`}>
                    {averageScore}%
                  </p>
                  <p className={`text-xs mt-2 ${getQuizScoreTextColorClass(averageScore)}`}>
                    {formatIndividualScores()}
                  </p>
                </div>
                
              </div>
            </div>
            
            {/* Score Description - Right Side */}
            <div className="md:w-2/3 md:pl-4">
              <h5 className="text-lg font-medium text-gray-800 mb-2">Challenge(s) Performance</h5>
              <p className="text-gray-600">
                This score represents the candidate's overall performance across all assigned challenges.
              </p>
            </div>
          </div>
        </div>
        
        {/* Cheating Probability Column */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col">
            <div className="flex flex-col md:flex-row">
              {/* Cheating Score Display - Left Side */}
              <div className="md:w-1/3 flex flex-col items-start mb-4 md:mb-0">
                <div className="w-full max-w-[200px]">
                  <div className={`text-center p-6 rounded-lg ${getCheatingProbabilityBgColorClass(averageCheatingProbability)}`}>
                    <p className={`text-5xl font-bold ${getCheatingProbabilityTextColorClass(averageCheatingProbability)}`}>
                      {getCheatingProbabilityLabel(averageCheatingProbability)}
                    </p>
                    <p className={`text-xs mt-2 ${getCheatingProbabilityTextColorClass(averageCheatingProbability)}`}>
                      {formatIndividualCheatingProbabilities()}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Cheating Description - Right Side */}
              <div className="md:w-2/3 md:pl-4">
                <h5 className="text-lg font-medium text-gray-800 mb-2">Cheating Probability</h5>
                <p className="text-gray-600">
                  This metric indicates the likelihood that the candidate used unauthorized assistance
                  during the challenges. 
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationsOverview;
